import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  signal,
} from '@angular/core';
import { UiButtonComponent } from '../../atoms/button/button.component';
import {
  DataGridColumn,
  DataTableColumnSizing,
  parseColumnMultiFilter,
  SortState,
  UiDataTableComponent,
} from '../data-table/data-table.component';

type TransferSide = 'left' | 'right';
type TransferDirection = 'to-left' | 'to-right';

@Component({
  selector: 'ui-table-transfer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiButtonComponent, UiDataTableComponent],
  templateUrl: './table-transfer.component.html',
  styleUrl: './table-transfer.component.scss',
})
export class UiTableTransferComponent {
  readonly rowKey = input.required<string>();
  readonly leftTitle = input('Disponibles');
  readonly leftSubtitle = input('');
  readonly rightTitle = input('Seleccionados');
  readonly rightSubtitle = input('');
  readonly leftColumns = input.required<DataGridColumn[]>();
  readonly rightColumns = input<DataGridColumn[] | null>(null);
  readonly leftRows = model<unknown[]>([]);
  readonly rightRows = model<unknown[]>([]);
  readonly showColumnFilters = input(true);
  readonly sortingEnabled = input(true);
  readonly showPagination = input(false);
  readonly showPageSize = input(false);
  readonly pageSize = input(10);
  readonly columnSizing = input<DataTableColumnSizing>('fixed');
  readonly verticalScroll = input(true);
  protected readonly tableHostHeight = computed(() => {
    if (!this.verticalScroll()) return null;
    const headerHeight = 37;
    const filterHeight = this.showColumnFilters() ? 48 : 0;
    const rowHeight = 40;
    const scrollbarAllowance = 12;
    return `${
      headerHeight +
      filterHeight +
      this.pageSize() * rowHeight +
      scrollbarAllowance
    }px`;
  });

  protected readonly leftSelectedKeys = signal<(string | number)[]>([]);
  protected readonly rightSelectedKeys = signal<(string | number)[]>([]);
  private readonly leftFilters = signal<Record<string, string>>({});
  private readonly rightFilters = signal<Record<string, string>>({});
  private readonly leftSort = signal<SortState | null>(null);
  private readonly rightSort = signal<SortState | null>(null);

  protected readonly effectiveRightColumns = computed(
    () => this.rightColumns() ?? this.leftColumns(),
  );
  protected readonly leftViewRows = computed(() =>
    this.tableRows('left', this.leftRows(), this.leftColumns()),
  );
  protected readonly rightViewRows = computed(() =>
    this.tableRows('right', this.rightRows(), this.effectiveRightColumns()),
  );

  protected readonly canMoveSelectedRight = computed(
    () => this.transferableRows('left', false).length > 0,
  );
  protected readonly canMoveAllRight = computed(
    () => this.transferableRows('left', true).length > 0,
  );
  protected readonly canMoveSelectedLeft = computed(
    () => this.transferableRows('right', false).length > 0,
  );
  protected readonly canMoveAllLeft = computed(
    () => this.transferableRows('right', true).length > 0,
  );

  protected onFilters(side: TransferSide, filters: Record<string, string>): void {
    this.filtersFor(side).set(filters);
  }

  protected onSort(side: TransferSide, sort: SortState): void {
    this.sortFor(side).set(sort);
  }

  protected moveSelected(direction: TransferDirection): void {
    this.move(direction, false);
  }

  protected moveAll(direction: TransferDirection): void {
    this.move(direction, true);
  }

  private move(direction: TransferDirection, allVisible: boolean): void {
    const sourceSide: TransferSide = direction === 'to-right' ? 'left' : 'right';
    const targetSide: TransferSide = sourceSide === 'left' ? 'right' : 'left';
    const sourceRows = this.rowsFor(sourceSide)();
    const targetRows = this.rowsFor(targetSide)();
    const candidates = this.transferableRows(sourceSide, allVisible);
    const targetKeys = new Set(
      targetRows.map(row => this.rowId(row)).filter(this.isRowKey),
    );
    const moved = candidates.filter(row => {
      const key = this.rowId(row);
      return key != null && !targetKeys.has(key);
    });
    if (moved.length === 0) return;

    const movedKeys = new Set(
      moved.map(row => this.rowId(row)).filter(this.isRowKey),
    );
    this.rowsFor(sourceSide).set(
      sourceRows.filter(row => {
        const key = this.rowId(row);
        return key == null || !movedKeys.has(key);
      }),
    );
    this.rowsFor(targetSide).set([...targetRows, ...moved]);
    this.selectedKeysFor(sourceSide).set([]);
  }

  private transferableRows(side: TransferSide, allVisible: boolean): unknown[] {
    const rows = side === 'left' ? this.leftViewRows() : this.rightViewRows();
    if (allVisible) return rows;
    const selected = new Set(this.selectedKeysFor(side)());
    return rows.filter(row => {
      const key = this.rowId(row);
      return key != null && selected.has(key);
    });
  }

  private tableRows(
    side: TransferSide,
    rows: readonly unknown[],
    columns: readonly DataGridColumn[],
  ): unknown[] {
    const filters = this.filtersFor(side)();
    const filtered = rows.filter(row =>
      columns.every(column =>
        this.matchesFilter(row, column, filters[column.key] ?? ''),
      ),
    );
    const sort = this.sortFor(side)();
    if (!sort) return filtered;
    const column = columns.find(item => item.key === sort.sortBy);
    if (!column) return filtered;
    const direction = sort.order === 'asc' ? 1 : -1;
    return [...filtered].sort(
      (left, right) =>
        direction *
        this.compareValues(
          this.cellValue(left, column.key),
          this.cellValue(right, column.key),
          column.type,
        ),
    );
  }

  private matchesFilter(
    row: unknown,
    column: DataGridColumn,
    filter: string,
  ): boolean {
    if (!filter || column.filterType === 'none') return true;
    const value = this.normalize(this.cellValue(row, column.key));
    if (column.filterType === 'select') return value === this.normalize(filter);
    if (column.filterType === 'multiselect') {
      const selected = parseColumnMultiFilter(filter).map(item =>
        this.normalize(item),
      );
      return selected.length === 0 || selected.includes(value);
    }
    return value.includes(this.normalize(filter));
  }

  private compareValues(
    left: unknown,
    right: unknown,
    type: DataGridColumn['type'],
  ): number {
    if (type === 'number' || type === 'currency') {
      return this.numberValue(left) - this.numberValue(right);
    }
    if (type === 'date') {
      return this.dateValue(left) - this.dateValue(right);
    }
    return String(left ?? '').localeCompare(String(right ?? ''), 'es', {
      numeric: true,
      sensitivity: 'base',
    });
  }

  private numberValue(value: unknown): number {
    if (typeof value === 'number') return value;
    const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private dateValue(value: unknown): number {
    const text = String(value ?? '');
    const mexicanDate = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
    const date = mexicanDate
      ? new Date(
          Number(mexicanDate[3]),
          Number(mexicanDate[2]) - 1,
          Number(mexicanDate[1]),
        )
      : new Date(text);
    const timestamp = date.getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  }

  private normalize(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('es-MX')
      .trim();
  }

  private cellValue(row: unknown, key: string): unknown {
    return row != null && typeof row === 'object'
      ? (row as Record<string, unknown>)[key]
      : null;
  }

  private rowId(row: unknown): string | number | null {
    const value = this.cellValue(row, this.rowKey());
    return this.isRowKey(value) ? value : null;
  }

  private readonly isRowKey = (
    value: unknown,
  ): value is string | number =>
    typeof value === 'string' || typeof value === 'number';

  private rowsFor(side: TransferSide) {
    return side === 'left' ? this.leftRows : this.rightRows;
  }

  private selectedKeysFor(side: TransferSide) {
    return side === 'left'
      ? this.leftSelectedKeys
      : this.rightSelectedKeys;
  }

  private filtersFor(side: TransferSide) {
    return side === 'left' ? this.leftFilters : this.rightFilters;
  }

  private sortFor(side: TransferSide) {
    return side === 'left' ? this.leftSort : this.rightSort;
  }
}
